import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_CONSTRAINTS = Object.freeze({ minWidth: 8, maxWidth: 400 });
const EMPTY_ORDER = Object.freeze({});
const EMPTY_HIDDEN = Object.freeze([]);
const SAVE_DEBOUNCE_MS = 500;

function isColumnGroup(column) {
    return Array.isArray(column?.children);
}

function flattenLeafColumns(columns = []) {
    return columns.flatMap((column) => (isColumnGroup(column) ? flattenLeafColumns(column.children) : [column]));
}

function normalizeConstraints(constraints = {}) {
    const minWidth = Number.isFinite(Number(constraints.minWidth)) ? Number(constraints.minWidth) : DEFAULT_CONSTRAINTS.minWidth;
    const rawMaxWidth = Number.isFinite(Number(constraints.maxWidth)) ? Number(constraints.maxWidth) : DEFAULT_CONSTRAINTS.maxWidth;
    return {
        minWidth,
        maxWidth: Math.max(minWidth, rawMaxWidth),
    };
}

function clampWidth(width, constraints) {
    const numericWidth = Number(width);
    if (!Number.isFinite(numericWidth)) return null;
    return Math.min(Math.max(Math.round(numericWidth), constraints.minWidth), constraints.maxWidth);
}

function buildDefaultWidthState(initialColumns, constraints) {
    return flattenLeafColumns(initialColumns).reduce((widthState, column) => {
        if (!column?.key) return widthState;
        const width = clampWidth(column.width ?? column.minWidth ?? constraints.minWidth, constraints);
        if (width !== null) widthState[column.key] = width;
        return widthState;
    }, {});
}

function normalizeWidthState(value, defaultWidthState, constraints) {
    const rawWidthState = value?.width && typeof value.width === "object" ? value.width : value;
    const source = rawWidthState && typeof rawWidthState === "object" ? rawWidthState : {};

    return Object.keys(defaultWidthState).reduce((widthState, columnKey) => {
        const width = clampWidth(source[columnKey], constraints);
        widthState[columnKey] = width ?? defaultWidthState[columnKey];
        return widthState;
    }, {});
}

function areWidthStatesEqual(left, right) {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;
    return leftKeys.every((key) => left[key] === right[key]);
}

function buildColumnState(width, changedWidthKey = null) {
    return {
        width,
        changedWidthKey,
        order: EMPTY_ORDER,
        hidden: EMPTY_HIDDEN,
    };
}

export function useGridColumnState({ initialColumns = [], persistence, constraints } = {}) {
    const normalizedConstraints = useMemo(() => normalizeConstraints(constraints), [constraints]);
    const defaultWidthState = useMemo(
        () => buildDefaultWidthState(initialColumns, normalizedConstraints),
        [initialColumns, normalizedConstraints],
    );
    const [widthState, setWidthState] = useState(() => defaultWidthState);
    const [isLoaded, setIsLoaded] = useState(() => !persistence);
    const defaultWidthStateRef = useRef(defaultWidthState);
    const constraintsRef = useRef(normalizedConstraints);
    const changedWidthKeyRef = useRef(null);
    const saveVersionRef = useRef(0);
    const saveRequestedRef = useRef(false);

    defaultWidthStateRef.current = defaultWidthState;
    constraintsRef.current = normalizedConstraints;

    useEffect(() => {
        setWidthState((currentWidthState) => {
            const nextWidthState = normalizeWidthState(currentWidthState, defaultWidthState, normalizedConstraints);
            const widthStateChanged = !areWidthStatesEqual(currentWidthState, nextWidthState);
            if (widthStateChanged) changedWidthKeyRef.current = null;
            return widthStateChanged ? nextWidthState : currentWidthState;
        });
    }, [defaultWidthState, normalizedConstraints]);

    useEffect(() => {
        if (!persistence) {
            setIsLoaded(true);
            return undefined;
        }

        let cancelled = false;
        setIsLoaded(false);

        async function loadColumnState() {
            try {
                const persistedWidthState = await persistence.load();
                if (cancelled) return;
                setWidthState((currentWidthState) => {
                    const nextWidthState = normalizeWidthState(
                        persistedWidthState ?? currentWidthState,
                        defaultWidthStateRef.current,
                        constraintsRef.current,
                    );
                    const widthStateChanged = !areWidthStatesEqual(currentWidthState, nextWidthState);
                    if (widthStateChanged) changedWidthKeyRef.current = null;
                    return widthStateChanged ? nextWidthState : currentWidthState;
                });
            } catch (error) {
                console.error("加载 GridCore 列宽配置失败", error);
            } finally {
                if (!cancelled) setIsLoaded(true);
            }
        }

        loadColumnState();
        return () => {
            cancelled = true;
        };
    }, [persistence]);

    useEffect(() => {
        if (!persistence || !isLoaded || !saveRequestedRef.current) return undefined;

        const saveVersion = saveVersionRef.current;
        const widthSnapshot = widthState;
        const saveTimer = window.setTimeout(() => {
            persistence
                .save(widthSnapshot)
                .catch((error) => {
                    console.error("保存 GridCore 列宽配置失败", error);
                })
                .finally(() => {
                    if (saveVersionRef.current === saveVersion) saveRequestedRef.current = false;
                });
        }, SAVE_DEBOUNCE_MS);

        return () => window.clearTimeout(saveTimer);
    }, [isLoaded, persistence, widthState]);

    const setColumnWidth = useCallback((key, width) => {
        if (!key) return;

        const nextWidth = clampWidth(width, constraintsRef.current);
        if (nextWidth === null) return;

        setWidthState((currentWidthState) => {
            if (currentWidthState[key] === nextWidth) return currentWidthState;
            changedWidthKeyRef.current = key;
            saveRequestedRef.current = true;
            saveVersionRef.current += 1;
            return {
                ...currentWidthState,
                [key]: nextWidth,
            };
        });
    }, []);

    const resetToDefault = useCallback(() => {
        setWidthState((currentWidthState) => {
            const nextWidthState = defaultWidthStateRef.current;
            if (areWidthStatesEqual(currentWidthState, nextWidthState)) return currentWidthState;
            changedWidthKeyRef.current = null;
            saveRequestedRef.current = true;
            saveVersionRef.current += 1;
            return nextWidthState;
        });
    }, []);

    const state = useMemo(() => buildColumnState(widthState, changedWidthKeyRef.current), [widthState]);

    return {
        state,
        isLoaded,
        setColumnWidth,
        resetToDefault,
    };
}

export default useGridColumnState;
